import { useEffect, useState } from 'react';
import { DistrictFormattedData } from '../../features/danh-muc/khac/danh-muc-quan-huyen/schemas';
import { QuanHuyen, QuanHuyenResponse } from '@/types/schemas/quan-huyen.type';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseQuanHuyenReturn {
  districts: QuanHuyen[];
  isLoading: boolean;
  addDistrict: (newDistrict: DistrictFormattedData) => Promise<void>;
  updateDistrict: (uuid: string, updatedDistrict: DistrictFormattedData) => Promise<void>;
  deleteDistrict: (uuid: string) => Promise<void>;
  refreshDistricts: () => Promise<void>;
  getDistrictsByProvince: (provinceId: string) => Promise<QuanHuyen[]>;
}

export const useQuanHuyen = (initialDistricts: QuanHuyen[] = []): UseQuanHuyenReturn => {
  const [districts, setDistricts] = useState<QuanHuyen[]>(initialDistricts);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  // Lấy danh sách quận/huyện
  const fetchDistricts = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      // Lấy danh sách quận/huyện với thông tin tỉnh/thành
      const response = await api.get<QuanHuyenResponse>(`/entities/${entity.slug}/erp/districts/?expand=tinh_thanh`);
      const mappedData: QuanHuyen[] = response.data.results;

      const enhancedData = await Promise.all(
        mappedData.map(async district => {
          if (district.ma_tinh && !district.tinh_thanh) {
            try {
              const provinceResponse = await api.get(`/entities/${entity.slug}/erp/provinces/${district.ma_tinh}/`);
              return {
                ...district,
                tinh_thanh: provinceResponse.data
              };
            } catch (err) {
              console.error(`Error fetching province for district ${district.ma_quan}:`, err);
              return district;
            }
          }
          return district;
        })
      );

      setDistricts(enhancedData);
    } catch (error) {
      console.error('Error fetching districts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Lấy danh sách quận/huyện theo tỉnh/thành
  const getDistrictsByProvince = async (provinceId: string): Promise<QuanHuyen[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<QuanHuyenResponse>(
        `/entities/${entity.slug}/erp/districts/by_province/?ma_tinh=${provinceId}&expand=tinh_thanh`
      );

      const districts = response.data.results;

      // Nếu cần, có thể lấy thêm thông tin tỉnh/thành
      if (provinceId) {
        try {
          const provinceResponse = await api.get(`/entities/${entity.slug}/erp/provinces/${provinceId}/`);
          const provinceData = provinceResponse.data;

          // Thêm thông tin tỉnh/thành vào các quận/huyện nếu chưa có
          return districts.map(district => {
            if (!district.tinh_thanh) {
              return {
                ...district,
                tinh_thanh: provinceData
              };
            }
            return district;
          });
        } catch (err) {
          console.error(`Error fetching province data:`, err);
        }
      }

      return districts;
    } catch (error) {
      console.error('Error fetching districts by province:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // Thêm mới quận/huyện
  const addDistrict = async (newDistrict: DistrictFormattedData) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const requestData = {
        ma_quan: newDistrict.ma_quan,
        ten_quan: newDistrict.ten_quan,
        ten_quan2: newDistrict.ten_quan2 || null,
        ma_tinh: newDistrict.ma_tinh || null,
        status: newDistrict.status || '1'
      };

      const response = await api.post(`/entities/${entity.slug}/erp/districts/`, requestData);

      let addedDistrict: QuanHuyen = response.data;

      // Nếu có mã tỉnh, lấy thông tin tỉnh/thành
      if (addedDistrict.ma_tinh && !addedDistrict.tinh_thanh) {
        try {
          const provinceResponse = await api.get(`/entities/${entity.slug}/erp/provinces/${addedDistrict.ma_tinh}/`);
          addedDistrict = {
            ...addedDistrict,
            tinh_thanh: provinceResponse.data
          };
        } catch (err) {
          console.error(`Error fetching province for new district:`, err);
        }
      }

      setDistricts(prev => [...prev, addedDistrict]);

      // Refresh the list to ensure we have the latest data
      await fetchDistricts();
    } catch (error) {
      console.error('Error adding district:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Cập nhật quận/huyện
  const updateDistrict = async (uuid: string, updatedDistrict: DistrictFormattedData) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const requestData = {
        ma_quan: updatedDistrict.ma_quan,
        ten_quan: updatedDistrict.ten_quan,
        ten_quan2: updatedDistrict.ten_quan2 || null,
        ma_tinh: updatedDistrict.ma_tinh || null,
        status: updatedDistrict.status || '1'
      };

      const response = await api.patch(`/entities/${entity.slug}/erp/districts/${uuid}/`, requestData);

      // Update the district in the list
      let updatedDistrictData: QuanHuyen = response.data;

      // Nếu có mã tỉnh, lấy thông tin tỉnh/thành
      if (updatedDistrictData.ma_tinh && !updatedDistrictData.tinh_thanh) {
        try {
          const provinceResponse = await api.get(
            `/entities/${entity.slug}/erp/provinces/${updatedDistrictData.ma_tinh}/`
          );
          updatedDistrictData = {
            ...updatedDistrictData,
            tinh_thanh: provinceResponse.data
          };
        } catch (err) {
          console.error(`Error fetching province for updated district:`, err);
        }
      }

      setDistricts(prev =>
        prev.map(district => (district.uuid === updatedDistrictData.uuid ? updatedDistrictData : district))
      );

      // Refresh the list to ensure we have the latest data
      await fetchDistricts();
    } catch (error) {
      console.error('Error updating district:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Xóa quận/huyện
  const deleteDistrict = async (uuid: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/districts/${uuid}/`);
      // Remove the district from the list
      setDistricts(prev => prev.filter(district => district.uuid !== uuid));

      // Refresh the list to ensure we have the latest data
      await fetchDistricts();
    } catch (error) {
      console.error('Error deleting district:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDistricts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.slug]);

  return {
    districts,
    isLoading,
    addDistrict,
    updateDistrict,
    deleteDistrict,
    refreshDistricts: fetchDistricts,
    getDistrictsByProvince
  };
};
